import { useState, useMemo, useEffect, lazy, Suspense, memo, useCallback, startTransition } from "react";
import { HashRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { supabase } from "./lib/supabase";
import { mockApartments } from "./data/mockApartments";
import { Apartment, Filters, Message, MeetingRequest } from "./types";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner@2.0.3";
import { useUpdateNotification } from "./hooks/useUpdateNotification";
import { useViewportHeight } from "./hooks/useViewportHeight";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { generateVibeAnswer, saveVibeCheckQuestion, fetchRenterQuestionsForApartment } from "./lib/vibeCheck";
import { Loader2 } from "lucide-react";
import { followBroker } from "./lib/brokerStories";
import { getProfile, ensureProfile, saveSearchPreferences } from "./lib/profiles";
import { queryClient } from "./lib/queryClient";
import { usePrefetchFavorites, useInvalidateFavorites } from "./hooks/useFavorites";
import { syncGuestSwipesToBackend } from "./hooks/useGuestSwipesSync";

// Eager load critical components (needed for initial render)
import { LoginView } from "./components/LoginView";
import { AIVibeCheckModal } from "./components/AIVibeCheckModal";
import { AppShell } from "./components/AppShell";

// Lazy load route components for code splitting
const DiscoverView = lazy(() => import("./components/DiscoverView").then(m => ({ default: m.DiscoverView })));
const PreferencesView = lazy(() => import("./components/PreferencesView").then(m => ({ default: m.PreferencesView })));
const FavoritesView = lazy(() => import("./components/FavoritesView").then(m => ({ default: m.FavoritesView })));
const ListingsView = lazy(() => import("./components/ListingsView").then(m => ({ default: m.ListingsView })));
const ApartmentPage = lazy(() => import("./pages/ApartmentPage").then(m => ({ default: m.ApartmentPage })));
const DealPage = lazy(() => import("./pages/DealPage").then(m => ({ default: m.DealPage })));
const InvitationPage = lazy(() => import("./pages/InvitationPage").then(m => ({ default: m.InvitationPage })));
const EnrichListingPage = lazy(() => import("./pages/EnrichListingPage").then(m => ({ default: m.EnrichListingPage })));
const MeetingRequestsPage = lazy(() => import("./pages/MeetingRequestsPage").then(m => ({ default: m.MeetingRequestsPage })));
const ClosingPage = lazy(() => import("./pages/ClosingPage").then(m => ({ default: m.ClosingPage })));
const BrokerProfilePage = lazy(() => import("./pages/BrokerProfilePage").then(m => ({ default: m.BrokerProfilePage })));
const BrokerStoriesPage = lazy(() => import("./pages/BrokerStoriesPage").then(m => ({ default: m.BrokerStoriesPage })));
const AudienceInsightsPage = lazy(() => import("./pages/AudienceInsightsPage").then(m => ({ default: m.AudienceInsightsPage })));
// Landing page (isolated module with its own CSS)
const LandingPage = lazy(() => import("./landing"));
const CompliancePage = lazy(() => import("./landing/components/CompliancePage").then(m => ({ default: m.CompliancePage })));
const AboutPage = lazy(() => import("./landing/components/AboutPage").then(m => ({ default: m.AboutPage })));
const TermsPage = lazy(() => import("./landing/components/TermsPage").then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("./landing/components/PrivacyPage").then(m => ({ default: m.PrivacyPage })));
const ContactPage = lazy(() => import("./landing/components/ContactPage").then(m => ({ default: m.ContactPage })));

// Admin pages (lazy loaded)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage").then(m => ({ default: m.AdminUsersPage })));
const AdminListingsPage = lazy(() => import("./pages/admin/AdminListingsPage").then(m => ({ default: m.AdminListingsPage })));
const AdminUploadPage = lazy(() => import("./pages/admin/AdminUploadPage").then(m => ({ default: m.AdminUploadPage })));
const AdminAnalyticsPage = lazy(() => import("./pages/admin/AdminAnalyticsPage").then(m => ({ default: m.AdminAnalyticsPage })));
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";

// Stable AuthenticatedLayout - defined outside MainApp to prevent remounting on state changes
const AuthenticatedLayout = memo(function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  // Initialize viewport height tracking - this adds 'keyboard-open' class to body when keyboard is visible
  useViewportHeight();
  
  // Prefetch favorites data on app load for zero-latency navigation
  usePrefetchFavorites();
  
  return <AppShell>{children}</AppShell>;
});

// Shared loading fallback component
const RouteLoadingFallback = () => (
  <div 
    className="fixed inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center"
    style={{ height: 'var(--app-height, 100dvh)' }}
  >
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Skeleton loader for DiscoverView - shows while the component/data loads
const DiscoverLoadingSkeleton = () => (
  <div className="h-full flex flex-col p-4 relative overflow-hidden bg-gray-50">
    {/* Card skeleton */}
    <div className="flex-1 relative" style={{ marginBottom: '20px' }}>
      <div className="absolute inset-0 bg-white rounded-3xl shadow-xl overflow-hidden animate-pulse">
        {/* Image skeleton */}
        <div className="h-[65%] bg-gradient-to-br from-gray-200 to-gray-300" />
        
        {/* Content skeleton */}
        <div className="p-5 space-y-4">
          {/* Title skeleton */}
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded-lg w-3/4" />
            <div className="h-4 bg-gray-200 rounded-lg w-1/2" />
          </div>
          
          {/* Details skeleton */}
          <div className="flex gap-4">
            <div className="h-4 bg-gray-200 rounded-lg w-20" />
            <div className="h-4 bg-gray-200 rounded-lg w-16" />
            <div className="h-4 bg-gray-200 rounded-lg w-24" />
          </div>
          
          {/* Price skeleton */}
          <div className="h-8 bg-gray-200 rounded-lg w-32" />
        </div>
      </div>
    </div>
    
    {/* Action buttons skeleton */}
    <div className="flex justify-center items-end gap-4 pb-2 relative z-40">
      <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
      <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse" />
      <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
    </div>
  </div>
);


// Optimized Protected Route wrapper with auth caching
// Uses sessionStorage to survive HMR (Hot Module Reload) during development
// Session-scoped to prevent stale auth state across different user sessions
const AUTH_CACHE_KEY = 'padswipe_auth_cache';

function getAuthCache(): { isAuthenticated: boolean; checked: boolean; sessionId: string | null } {
  try {
    const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // sessionStorage unavailable (incognito) - fall back to memory
  }
  return { isAuthenticated: false, checked: false, sessionId: null };
}

function setAuthCache(cache: { isAuthenticated: boolean; checked: boolean; sessionId: string | null }) {
  try {
    sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // sessionStorage unavailable - ignore
  }
}

function clearAuthCache() {
  try {
    sessionStorage.removeItem(AUTH_CACHE_KEY);
  } catch (e) {
    // ignore
  }
}


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const cachedAuth = getAuthCache();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(
    cachedAuth.checked ? cachedAuth.isAuthenticated : null
  );
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = (session: any) => {
      const authenticated = !!session;
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        setAuthCache({
          isAuthenticated: true,
          checked: true,
          sessionId: session.user.id
        });
      } else {
        clearAuthCache();
        navigate('/login', { replace: true });
      }
    };
    
    // 1. Check current session with timeout fallback (getSession can hang in incognito)
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 500));
    
    Promise.race([sessionPromise, timeoutPromise]).then((result) => {
      if (result && 'data' in result) {
        // getSession resolved successfully
        checkAuth(result.data.session);
      } else {
        // Timeout - use cached auth state or redirect
        if (cachedAuth.checked && cachedAuth.isAuthenticated) {
          setIsAuthenticated(true);
        } else {
          clearAuthCache();
          navigate('/login', { replace: true });
        }
      }
    });
    
    // 2. Listen for changes (handles sign out, token refresh, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        checkAuth(null);
      } else if (session) {
        checkAuth(session);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [navigate]);

  if (isAuthenticated === null) {
    return <RouteLoadingFallback />;
  }

  return isAuthenticated ? <>{children}</> : null;
}

// Reset auth cache on logout - clears all session-related state
const resetAuthCache = () => {
  clearAuthCache();
};

// Public Route - shows landing page for unauthenticated users, redirects to /discover for authenticated
function PublicLandingRoute() {
  const cachedAuth = getAuthCache();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(
    cachedAuth.checked ? cachedAuth.isAuthenticated : null
  );
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = (session: any) => {
      const authenticated = !!session;
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        setAuthCache({
          isAuthenticated: true,
          checked: true,
          sessionId: session.user.id
        });
        navigate('/discover', { replace: true });
      } else {
        setAuthCache({
          isAuthenticated: false,
          checked: true,
          sessionId: null
        });
      }
    };
    
    // 1. Check current session with timeout fallback (getSession can hang in incognito)
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 500));
    
    Promise.race([sessionPromise, timeoutPromise]).then((result) => {
      if (result && 'data' in result) {
        // getSession resolved successfully
        checkAuth(result.data.session);
      } else {
        // Timeout - treat as not authenticated (show landing page)
        setIsAuthenticated(false);
        setAuthCache({
          isAuthenticated: false,
          checked: true,
          sessionId: null
        });
      }
    });
    
    // 2. Listen for changes (handles sign in from this page)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        checkAuth(session);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [navigate]);

  if (isAuthenticated === null) {
    return <RouteLoadingFallback />;
  }

  // Show landing page for unauthenticated users
  return isAuthenticated ? null : (
    <Suspense fallback={<RouteLoadingFallback />}>
      <LandingPage />
    </Suspense>
  );
}

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setIsAuthenticated] = useState(false);
  const [apartments, setApartments] = useState<Apartment[]>(mockApartments); // Initialize with mock data immediately
  const [likedApartments, setLikedApartments] = useState<string[]>([]);
  const [swipedApartments, setSwipedApartments] = useState<string[]>([]); // Track all swiped apartments
  const [swipeHistory, setSwipeHistory] = useState<Array<{ apartmentId: string; action: 'like' | 'pass' }>>([]); // For undo feature
  
  // TanStack Query cache invalidation for favorites
  const invalidateFavoritesQueryCache = useInvalidateFavorites();
  
  // Check for app updates
  useUpdateNotification();
  // Removed selectedApartmentId and showDetailView - now handled by routing
  const [filters, setFilters] = useState<Filters>({
    minPrice: 0,
    maxPrice: 25000,
    minRooms: 1,
    maxRooms: 6,
    minSqm: 0,
    maxSqm: 300,
    petFriendly: null,
    amenities: [],
    location: null,
    radius: 5,
    moveInUrgency: null,
    listingType: 'rent',
  });
  const [meetingRequests, setMeetingRequests] = useState<MeetingRequest[]>([]);

  // Chat state
  const [conversations, setConversations] = useState<Record<string, Message[]>>({});
  const [isAIResponding, setIsAIResponding] = useState<Record<string, boolean>>({});

  // AI Vibe Check Modal state - lifted to App level to survive component remounts
  const [aiVibeCheckModal, setAiVibeCheckModal] = useState<{
    isOpen: boolean;
    apartmentId: string | null;
    apartmentTitle: string;
  }>({ isOpen: false, apartmentId: null, apartmentTitle: '' });

  const openAIVibeCheckModal = useCallback((apartmentId: string, apartmentTitle: string) => {
    setAiVibeCheckModal({ isOpen: true, apartmentId, apartmentTitle });
  }, []);

  const closeAIVibeCheckModal = useCallback(() => {
    setAiVibeCheckModal({ isOpen: false, apartmentId: null, apartmentTitle: '' });
  }, []);

  useEffect(() => {
    // Check active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsAuthenticated(true);
        
        // Ensure user has a profile (for broker stories feature)
        ensureProfile();
        
        // Check if we need to restore route after service worker update
        const preUpdateRoute = sessionStorage.getItem('preUpdateRoute');
        if (preUpdateRoute) {
          sessionStorage.removeItem('preUpdateRoute');
          // Let the HashRouter handle the restoration naturally
          // (it will already be in the URL)
          fetchApartments();
          fetchLikedApartments(session.user.id);
          fetchSwipedApartments();
          return;
        }
        
        // Check for stored invitation token and redirect
        const invitationToken = localStorage.getItem('invitation_token');
        if (invitationToken) {
          console.log('[App] Found stored invitation token on mount, redirecting...');
          navigate(`/invite/${invitationToken}`);
          // Don't remove token here - InvitationPage will handle it
        } else {
          // Navigate to discover if on root or login
          if (location.pathname === '/' || location.pathname === '/login') {
            navigate('/discover', { replace: true });
          }
        }
        
        fetchApartments(); // This will replace mock data with DB data if available
        fetchLikedApartments(session.user.id);
        fetchSwipedApartments();
      } else {
        // Not authenticated - navigate to login if not already there
        // Get current pathname dynamically from HashRouter to avoid stale closure values
        const getCurrentPathname = () => {
          const hash = window.location.hash;
          if (hash) {
            const path = hash.replace('#', '').split('?')[0];
            return path || '/';
          }
          return location.pathname;
        };
        
        const currentPathname = getCurrentPathname();
        // Allow public routes: /, /login, /invite/*, /stories/*, /broker/*, /apartment/*, /compliance, /about, /terms, /privacy, /contact
        const isPublicRoute = currentPathname === '/login' || 
          currentPathname === '/' || 
          currentPathname.startsWith('/invite/') ||
          currentPathname.startsWith('/stories/') ||
          currentPathname.startsWith('/broker/') ||
          currentPathname.startsWith('/apartment/') ||
          currentPathname === '/compliance' ||
          currentPathname === '/about' ||
          currentPathname === '/terms' ||
          currentPathname === '/privacy' ||
          currentPathname === '/contact';
        if (!isPublicRoute) {
          navigate('/login', { replace: true });
        }
      }
      // If no session, keep the mock apartments that were initialized in useState
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        setIsAuthenticated(true);
        
        // Ensure user has a profile (for broker stories feature)
        ensureProfile();
        
        // Sync guest swipes to backend (likes become favorites, all swipes are tracked)
        await syncGuestSwipesToBackend(session.user.id);
        
        // Handle pending broker ref from shared links (Stories feature) - do this before redirect
        const pendingBrokerRef = localStorage.getItem('pending_broker_ref');
        if (pendingBrokerRef && pendingBrokerRef !== session.user.id) {
          localStorage.removeItem('pending_broker_ref');
          const success = await followBroker(pendingBrokerRef);
          if (success) {
            const profile = await getProfile(pendingBrokerRef);
            const brokerName = profile?.name || 'this broker';
            toast.success(`You are now following ${brokerName} for updates`);
          }
        }
        
        // Check for stored invitation token and redirect
        const invitationToken = localStorage.getItem('invitation_token');
        if (invitationToken) {
          console.log('[App] Found stored invitation token, redirecting...');
          navigate(`/invite/${invitationToken}`);
          // Don't remove token here - InvitationPage will handle it
        } else {
          // Check for pending vibeCheck state (from guest login in story mode)
          const pendingVibeCheckState = localStorage.getItem('pending_vibecheck_state');
          if (pendingVibeCheckState) {
            try {
              const vibeCheckState = JSON.parse(pendingVibeCheckState);
              localStorage.removeItem('pending_vibecheck_state');
              navigate(`/stories/${vibeCheckState.brokerId}`, {
                replace: true,
                state: {
                  restoreVibeCheck: true,
                  vibeCheckApartmentId: vibeCheckState.apartmentId,
                  vibeCheckApartmentTitle: vibeCheckState.apartmentTitle,
                  vibeCheckMessages: vibeCheckState.messages,
                },
              });
              return; // Don't continue with other redirects
            } catch (error) {
              console.error('[App] Error parsing pending vibeCheck state:', error);
              localStorage.removeItem('pending_vibecheck_state');
            }
          }
          
          // Check for pending redirect URL (from guest story mode actions)
          const pendingRedirectUrl = localStorage.getItem('pending_redirect_url');
          if (pendingRedirectUrl) {
            localStorage.removeItem('pending_redirect_url');
            navigate(pendingRedirectUrl, { replace: true });
          } else {
            // Legacy: Check for pending stories redirect to favorites
            const pendingStoriesRedirectToFavorites = localStorage.getItem('pending_stories_redirect_to_favorites');
            if (pendingStoriesRedirectToFavorites) {
              localStorage.removeItem('pending_stories_redirect_to_favorites');
              navigate('/favorites', { replace: true });
            } else if (location.pathname === '/login' || location.pathname === '/') {
          // Navigate to discover after login if on login page
            navigate('/discover', { replace: true });
            }
          }
        }
        
        fetchApartments(); // Fetch from DB when logged in
        fetchLikedApartments(session.user.id);
        fetchSwipedApartments();
      } else {
        setIsAuthenticated(false);
        resetAuthCache(); // Clear auth cache on logout
        
        // Get current pathname dynamically from HashRouter (not from closure)
        // HashRouter stores the route in window.location.hash
        // This ensures we always check the ACTUAL current route, not a stale closure value
        const getCurrentPathname = () => {
          // For HashRouter, the path is in the hash without the #
          const hash = window.location.hash;
          if (hash) {
            const path = hash.replace('#', '').split('?')[0]; // Remove # and query params
            return path || '/';
          }
          // Fallback to location.pathname if no hash (shouldn't happen with HashRouter)
          return location.pathname;
        };
        
        const currentPathname = getCurrentPathname();
        // Only redirect to login if not on allowed public routes
        // Allow public routes: /, /login, /invite/*, /stories/*, /broker/*, /apartment/*, /compliance, /about, /terms, /privacy, /contact
        const isPublicRoute = currentPathname === '/login' || 
          currentPathname === '/' || 
          currentPathname.startsWith('/invite/') ||
          currentPathname.startsWith('/stories/') ||
          currentPathname.startsWith('/broker/') ||
          currentPathname.startsWith('/apartment/') ||
          currentPathname === '/compliance' ||
          currentPathname === '/about' ||
          currentPathname === '/terms' ||
          currentPathname === '/privacy' ||
          currentPathname === '/contact';
        if (!isPublicRoute) {
        navigate('/login', { replace: true });
        }
        setLikedApartments([]);
        setSwipedApartments([]);
        // Keep mock apartments on logout
        setApartments(mockApartments);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location]);

  const fetchApartments = async () => {
    try {
      const { data, error } = await supabase
        .from('apartments')
        .select('*');

      if (error) throw error;

      // Use fetched data if available, otherwise keep mock data
      if (data && data.length > 0) {
        setApartments(data);
      }
      // If database is empty, keep the mock data from initial state
    } catch (error) {
      console.error('Error fetching apartments:', error);
      Sentry.captureException(error, { tags: { feature: 'apartments', action: 'fetch' } });
      // Keep mock data on error - don't call setApartments
    }
  };

  const fetchLikedApartments = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('matches')
        .select('apartment_id')
        .eq('renter_id', userId);

      if (error) throw error;

      const likedIds = data.map((match) => match.apartment_id);
      setLikedApartments(likedIds);
    } catch (error) {
      console.error('Error fetching liked apartments:', error);
      Sentry.captureException(error, { tags: { feature: 'favorites', action: 'fetch' } });
    }
  };

  const fetchSwipedApartments = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_swiped_apartment_ids');

      if (error) throw error;

      const swipedIds = data?.map((row: { apartment_id: string }) => row.apartment_id) || [];
      setSwipedApartments(swipedIds);
    } catch (error) {
      console.error('Error fetching swiped apartments:', error);
      Sentry.captureException(error, { tags: { feature: 'swipes', action: 'fetch' } });
    }
  };

  const recordSwipe = async (apartmentId: string, action: 'like' | 'pass') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Optimistic update
      setSwipedApartments((prev) => [...prev, apartmentId]);
      setSwipeHistory((prev) => [...prev, { apartmentId, action }]);

      const { error } = await supabase
        .from('apartment_swipes')
        .upsert({
          renter_id: user.id,
          apartment_id: apartmentId,
          action: action,
        }, { onConflict: 'renter_id,apartment_id' });

      if (error) throw error;
    } catch (error) {
      console.error('Error recording swipe:', error);
      Sentry.captureException(error, { tags: { feature: 'swipes', action: 'record' } });
      // Don't revert optimistic update - swipe is already processed in UI
    }
  };

  const handleLogin = useCallback(() => {
    setIsAuthenticated(true);
    setAuthCache({ isAuthenticated: true, checked: true, sessionId: null });
    navigate('/discover');
  }, [navigate]);

  const handleDislike = async (apartmentId: string) => {
    // Record the swipe as a pass
    recordSwipe(apartmentId, 'pass');
    
    // If the apartment is liked, remove it from matches
    if (likedApartments.includes(apartmentId)) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Optimistic update
        setLikedApartments((prev) => prev.filter((id) => id !== apartmentId));

        // Unlike: Delete from matches
        const { error } = await supabase
          .from('matches')
          .delete()
          .eq('renter_id', user.id)
          .eq('apartment_id', apartmentId);

        if (error) throw error;
      } catch (error: any) {
        console.error('Error removing like on dislike:', error);
        Sentry.captureException(error, { tags: { feature: 'favorites', action: 'remove' } });
        // Revert optimistic update on error
        setLikedApartments((prev) => [...prev, apartmentId]);
      }
    }
  };

  const handleCreateListing = (newApartment: Omit<Apartment, "id">) => {
    const apartment: Apartment = {
      ...newApartment,
      id: Date.now().toString(),
    };
    setApartments((prev) => [...prev, apartment]);
  };

  const handleSelectApartment = (apartmentId: string, currentApartmentId?: string) => {
    // Determine the from location based on current path
    const currentPath = location.pathname;
    let from = 'discover'; // default
    if (currentPath.startsWith('/favorites')) from = 'favorites';
    else if (currentPath.startsWith('/discover')) from = 'discover';
    
    navigate(`/apartment/${apartmentId}`, { 
      state: { 
        from,
        // Store the current apartment ID so we can restore position when going back
        restoreToApartmentId: currentApartmentId 
      } 
    });
  };

  const handleToggleFavorite = async (apartmentId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to like apartments");
        return;
      }

      // Invalidate TanStack Query cache on mutation
      invalidateFavoritesQueryCache();

      const isCurrentlyLiked = likedApartments.includes(apartmentId);

      // Optimistic update
      if (isCurrentlyLiked) {
        setLikedApartments((prev) => prev.filter((id) => id !== apartmentId));
      } else {
        setLikedApartments((prev) => [...prev, apartmentId]);
      }

      if (isCurrentlyLiked) {
        // Unlike: Delete from matches
        const { error } = await supabase
          .from('matches')
          .delete()
          .eq('renter_id', user.id)
          .eq('apartment_id', apartmentId);

        if (error) throw error;
      } else {
        // Like: Insert into matches
        const { error } = await supabase
          .from('matches')
          .insert({
            renter_id: user.id,
            apartment_id: apartmentId,
          });

        if (error) throw error;
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      Sentry.captureException(error, { tags: { feature: 'favorites', action: 'toggle' } });
      // Revert optimistic update on error
      if (likedApartments.includes(apartmentId)) {
        setLikedApartments((prev) => prev.filter((id) => id !== apartmentId));
      } else {
        setLikedApartments((prev) => [...prev, apartmentId]);
      }
      toast.error(`Failed to update favorite: ${error.message}`);
    }
  };

  const handleAddFavorite = async (apartmentId: string) => {
    // Record the swipe as a like
    recordSwipe(apartmentId, 'like');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to like apartments");
        return;
      }

      // Only add if not already liked
      if (likedApartments.includes(apartmentId)) {
        return; // Do nothing if already liked
      }

      // Optimistic update
      setLikedApartments((prev) => [...prev, apartmentId]);

      // Like: Insert into matches
      const { error } = await supabase
        .from('matches')
        .insert({
          renter_id: user.id,
          apartment_id: apartmentId,
        });

      // Handle duplicate key error (23505) gracefully - apartment already liked
      if (error) {
        if (error.code === '23505') {
          // Duplicate key - apartment already liked, just update state
          setLikedApartments((prev) => {
            if (!prev.includes(apartmentId)) {
              return [...prev, apartmentId];
            }
            return prev;
          });
          return; // Exit early, already liked
        }
        throw error; // Re-throw other errors
      }
    } catch (error: any) {
      console.error('Error adding favorite:', error);
      Sentry.captureException(error, { tags: { feature: 'favorites', action: 'add' } });
      // Revert optimistic update on error
      setLikedApartments((prev) => prev.filter((id) => id !== apartmentId));
      toast.error(`Failed to add favorite: ${error.message}`);
    }
  };

  const handleContactRequest = () => {
    // In a real app, this would save to the database
    toast.success("Contact request sent!");
  };

  const handleSearchAgain = async () => {
    try {
      // Clear all swipes from database
      const { error } = await supabase.rpc('clear_swipes');
      
      if (error) throw error;
      
      // Clear local state
      setSwipedApartments([]);
      setSwipeHistory([]);
      
      // Refetch apartments to get the fresh list
      fetchApartments();
      
      toast.success("Feed refreshed! Showing all apartments.");
    } catch (error) {
      console.error('Error clearing swipes:', error);
      Sentry.captureException(error, { tags: { feature: 'swipes', action: 'clear' } });
      toast.error("Failed to refresh feed. Please try again.");
    }
  };

  const handleUndoSwipe = async () => {
    if (swipeHistory.length === 0) return;
    
    // Get the last swipe
    const lastSwipe = swipeHistory[swipeHistory.length - 1];
    const { apartmentId, action } = lastSwipe;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Optimistic updates - remove from local state immediately
      setSwipeHistory((prev) => prev.slice(0, -1));
      setSwipedApartments((prev) => prev.filter((id) => id !== apartmentId));
      
      // If it was a like, also remove from liked apartments
      if (action === 'like') {
        setLikedApartments((prev) => prev.filter((id) => id !== apartmentId));
        
        // Delete from matches table
        await supabase
          .from('matches')
          .delete()
          .eq('renter_id', user.id)
          .eq('apartment_id', apartmentId);
      }
      
      // Delete from apartment_swipes table
      const { error } = await supabase
        .from('apartment_swipes')
        .delete()
        .eq('renter_id', user.id)
        .eq('apartment_id', apartmentId);

      if (error) throw error;
      
    } catch (error) {
      console.error('Error undoing swipe:', error);
      Sentry.captureException(error, { tags: { feature: 'swipes', action: 'undo' } });
      // Don't revert - the UI has already updated
      toast.error("Failed to undo. Please try again.");
    }
  };


  /**
   * Load previous questions and answers for an apartment from the database
   * This allows renters to see their conversation history when they return
   * Always loads from DB to ensure we have the latest saved data
   */
  const loadConversationHistory = async (apartmentId: string) => {
    try {
      const questions = await fetchRenterQuestionsForApartment(apartmentId);
      
      if (questions.length === 0) {
        // Use startTransition to prevent Suspense re-suspend when updating state
        startTransition(() => {
          setConversations((prev) => ({
            ...prev,
            [apartmentId]: [],
          }));
        });
        return;
      }

      // Convert database questions to Message format
      const messages: Message[] = [];
      questions.forEach((q: any) => {
        // Add user question
        messages.push({
          id: `q-${q.id}`,
          role: "user",
          content: q.question,
        });
        
        // Add AI answer (or landlord answer if available)
        const answerContent = q.landlord_answer || q.ai_answer;
        if (answerContent) {
          messages.push({
            id: `a-${q.id}`,
            role: "ai",
            content: answerContent,
          });
        }
      });

      // Use startTransition to prevent Suspense re-suspend when updating state
      startTransition(() => {
        setConversations((prev) => ({
          ...prev,
          [apartmentId]: messages,
        }));
      });
    } catch (error) {
      console.error('Error loading conversation history:', error);
      Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'load-history' } });
    }
  };

  const handleSendMessage = async (apartmentId: string, text: string) => {
    const apartment = apartments.find((a) => a.id === apartmentId);
    if (!apartment) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: text,
    };

    setConversations((prev) => ({
      ...prev,
      [apartmentId]: [...(prev[apartmentId] || []), userMessage],
    }));

    setIsAIResponding((prev) => ({ ...prev, [apartmentId]: true }));

    try {
      // Fetch existing questions to provide AI with learned knowledge
      const existingQuestions = await fetchRenterQuestionsForApartment(apartmentId);
      
      // Generate AI answer using the vibe check service with context
      const { answer, isGap } = await generateVibeAnswer(text, apartment, existingQuestions);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "ai",
        content: answer,
      };

      setConversations((prev) => ({
        ...prev,
        [apartmentId]: [...(prev[apartmentId] || []), aiMessage],
      }));

      // Save to database for landlord enrichment
      const saved = await saveVibeCheckQuestion(apartmentId, text, answer, isGap);
      if (!saved) {
        console.warn('Failed to save question to database');
        toast.error("Question wasn't saved to history, but here's your answer!");
      }
      
    } catch (error) {
      console.error('Error generating AI response:', error);
      Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'generate-response' } });
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "ai",
        content: "I'm having trouble processing your question right now. Please try again!",
      };
      setConversations((prev) => ({
        ...prev,
        [apartmentId]: [...(prev[apartmentId] || []), errorMessage],
      }));
    } finally {
      setIsAIResponding((prev) => ({ ...prev, [apartmentId]: false }));
    }
  };

  const handleUpdateMeetingRequest = (id: string, status: 'accepted' | 'declined') => {
    setMeetingRequests(prev => prev.map(m => m.id === id ? { ...m, status } : m));
  };

  // Filter apartments based on preferences and exclude already swiped
  const filteredApartments = useMemo(() => {
    return apartments.filter((apartment) => {
      // Exclude already swiped apartments
      if (swipedApartments.includes(apartment.id)) {
        return false;
      }

      // Listing type filter (rent vs sale)
      const apartmentListingType = apartment.listing_type || 'rent';
      if (apartmentListingType !== filters.listingType) {
        return false;
      }
      
      // Price filter
      if (
        apartment.price < filters.minPrice ||
        apartment.price > filters.maxPrice
      ) {
        return false;
      }

      // Rooms filter
      if (
        apartment.rooms < filters.minRooms ||
        (filters.maxRooms < 6 && apartment.rooms > filters.maxRooms)
      ) {
        return false;
      }

      // Sqm filter
      if (
        apartment.sqm < filters.minSqm ||
        (filters.maxSqm < 300 && apartment.sqm > filters.maxSqm)
      ) {
        return false;
      }

      // Pet friendly filter
      if (filters.petFriendly !== null && !apartment.petFriendly) {
        return false;
      }

      // Amenities filter
      if (filters.amenities.length > 0) {
        const hasAllAmenities = filters.amenities.every((amenity) =>
          apartment.amenities.includes(amenity)
        );
        if (!hasAllAmenities) {
          return false;
        }
      }

      return true;
    });
  }, [apartments, filters, swipedApartments]);


  // AuthenticatedLayout is now defined outside MainApp to prevent remounting on every render

  return (
    <>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginView onLogin={handleLogin} />} />
      <Route path="/invite/:token" element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <InvitationPage />
        </Suspense>
      } />
      
      {/* Protected routes with layout */}
      <Route path="/discover" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
              <div className="h-full w-full overflow-hidden overscroll-none">
                <Suspense fallback={<DiscoverLoadingSkeleton />}>
                  <DiscoverView
                    apartments={filteredApartments}
                    onLike={handleAddFavorite}
                    onDislike={handleDislike}
                    onContactRequest={handleContactRequest}
                    onSelectApartment={handleSelectApartment}
                    onOpenAIVibeCheck={openAIVibeCheckModal}
                    onSearchAgain={handleSearchAgain}
                    onUndo={handleUndoSwipe}
                    canUndo={swipeHistory.length > 0}
                    likedApartments={likedApartments}
                  />
                </Suspense>
              </div>
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/preferences" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
              <div className="h-full w-full overflow-y-auto overscroll-contain scroll-smooth-touch">
                <PreferencesView
                  filters={filters}
                  onFiltersChange={setFilters}
                  matchingCount={filteredApartments.length}
                />
              </div>
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/favorites" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
              <div className="h-full w-full overflow-y-auto overscroll-contain scroll-smooth-touch">
                <FavoritesView
                  onSelectApartment={handleSelectApartment}
                  onRemoveFavorite={handleToggleFavorite}
                />
              </div>
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/listings" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
                <ListingsView 
                  onCreateListing={handleCreateListing}
                  apartments={apartments}
                  vibeCheckQuestions={[]}
                  meetingRequests={meetingRequests}
                  onUpdateMeeting={handleUpdateMeetingRequest}
                  onDeleteListing={(id) => {
                    setApartments(prev => prev.filter(a => a.id !== id));
                  }}
                  onUpdateListing={(updatedApartment) => {
                    setApartments((prev) =>
                      prev.map((apt) => (apt.id === updatedApartment.id ? updatedApartment : apt))
                    );
                  }}
                />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />

      {/* Listing sub-routes (no bottom nav) */}
      <Route path="/listings/enrich/:apartmentId" element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <EnrichListingPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/listings/meetings/:apartmentId" element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <MeetingRequestsPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/listings/closing/:apartmentId" element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <ClosingPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/insights" element={
        <ProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <AudienceInsightsPage />
          </Suspense>
        </ProtectedRoute>
      } />

      {/* Admin routes - only accessible to users with admin role */}
      <Route path="/admin" element={
        <AdminProtectedRoute>
          <Suspense fallback={<RouteLoadingFallback />}>
            <AdminLayout />
          </Suspense>
        </AdminProtectedRoute>
      }>
        <Route index element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <AdminDashboardPage />
          </Suspense>
        } />
        <Route path="users" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <AdminUsersPage />
          </Suspense>
        } />
        <Route path="listings" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <AdminListingsPage />
          </Suspense>
        } />
        <Route path="upload" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <AdminUploadPage />
          </Suspense>
        } />
        <Route path="analytics" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <AdminAnalyticsPage />
          </Suspense>
        } />
      </Route>

      {/* Apartment detail (no bottom nav) - PUBLIC route for guest viewing from story mode or broker profile */}
      <Route path="/apartment/:id" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <ApartmentPage />
          </Suspense>
      } />

      {/* Deal page (no bottom nav) - PUBLIC route for viewing signed agreement */}
      <Route path="/apartment/:id/deal" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <DealPage />
          </Suspense>
      } />

      {/* Broker profile (no bottom nav) */}
      <Route path="/broker/:id" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <BrokerProfilePage />
          </Suspense>
      } />

      {/* Broker stories - shared link entry point (no bottom nav) - PUBLIC route for guest viewing */}
      <Route path="/stories/:id" element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <BrokerStoriesPage />
          </Suspense>
      } />

      {/* Compliance page - public route */}
      <Route path="/compliance" element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <CompliancePage />
        </Suspense>
      } />
      
      {/* About Page - Public */}
      <Route path="/about" element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <AboutPage />
        </Suspense>
      } />
      
      {/* Terms of Service - Public */}
      <Route path="/terms" element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <TermsPage />
        </Suspense>
      } />
      
      {/* Privacy Policy - Public */}
      <Route path="/privacy" element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <PrivacyPage />
        </Suspense>
      } />
      
      {/* Contact Page - Public */}
      <Route path="/contact" element={
        <Suspense fallback={<RouteLoadingFallback />}>
          <ContactPage />
        </Suspense>
      } />

      {/* Root - Landing page for unauthenticated, redirect to /discover for authenticated */}
      <Route path="/" element={<PublicLandingRoute />} />
    </Routes>

    {/* Global AI Vibe Check Modal - rendered at App level to survive component remounts */}
    {aiVibeCheckModal.isOpen && aiVibeCheckModal.apartmentId && (
      <AIVibeCheckModal
        isOpen={true}
        onClose={closeAIVibeCheckModal}
        apartmentTitle={aiVibeCheckModal.apartmentTitle}
        apartmentId={aiVibeCheckModal.apartmentId}
        messages={conversations[aiVibeCheckModal.apartmentId] || []}
        onSendMessage={(text) => handleSendMessage(aiVibeCheckModal.apartmentId!, text)}
        isLoading={isAIResponding[aiVibeCheckModal.apartmentId] || false}
        onLoadHistory={loadConversationHistory}
      />
    )}
    </>
  );
}

// Toaster wrapper that adjusts position based on route
function ToasterWrapper() {
  const location = useLocation();
  
  // Determine if current route is a sub-screen with back button
  // Sub-screens: ApartmentPage, EnrichListingView, MeetingRequestsView, ClosingView, 
  // BrokerAgreementsView, AvailabilityManager, InvitationPage, and ListingsView create/edit forms
  const isSubScreen = 
    location.pathname.startsWith('/apartment/') ||
    location.pathname.startsWith('/listings/enrich/') ||
    location.pathname.startsWith('/listings/meetings/') ||
    location.pathname.startsWith('/listings/closing/') ||
    location.pathname.startsWith('/listings/availability/') ||
    location.pathname.startsWith('/listings/broker-agreements/') ||
    location.pathname.startsWith('/invite/');
  
  useEffect(() => {
    const toaster = document.querySelector('[data-sonner-toaster]');
    if (toaster) {
      if (isSubScreen) {
        toaster.setAttribute('data-screen-type', 'subScreen');
      } else {
        toaster.removeAttribute('data-screen-type');
      }
    }
  }, [isSubScreen]);
  
  return <Toaster position="top-center" />;
}

// Global auth state using the standard "Check + Listen" pattern
// This is the canonical way to handle Supabase auth with OAuth
function useAuthState() {
  const [isReady, setIsReady] = useState(false);
  const [session, setSession] = useState<any>(null);
  
  useEffect(() => {
    // 1. Check for active session with timeout fallback (getSession can hang in incognito)
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 500));
    
    Promise.race([sessionPromise, timeoutPromise]).then((result) => {
      if (result && 'data' in result) {
        const currentSession = result.data.session;
        if (currentSession) {
          setSession(currentSession);
          setAuthCache({
            isAuthenticated: true,
            checked: true,
            sessionId: currentSession.user.id
          });
        }
      }
      
      // Mark as ready if no OAuth tokens in URL (those need listener to process)
      if (!window.location.hash.includes('access_token')) {
        setIsReady(true);
      }
    });
    
    // 2. Listen for auth state changes (handles OAuth redirects, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      
      if (newSession) {
        setAuthCache({
          isAuthenticated: true,
          checked: true,
          sessionId: newSession.user.id
        });
        
        // Clean OAuth hash from URL if present (prevents HashRouter confusion)
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.origin + window.location.pathname + '#/discover');
        }
      } else {
        clearAuthCache();
      }
      
      setIsReady(true);
    });
    
    // Fallback: If onAuthStateChange doesn't fire within 1s (for OAuth), proceed anyway
    const fallbackTimer = setTimeout(() => {
      setIsReady(true);
    }, 1000);
    
    return () => {
      subscription.unsubscribe();
      clearTimeout(fallbackTimer);
    };
  }, []);
  
  return { isReady, session };
}

// Export App with Router
export default function App() {
  const { isReady } = useAuthState();
  
  // Show loading while waiting for auth to initialize
  if (!isReady) {
    return <RouteLoadingFallback />;
  }
  
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <HashRouter>
            <MainApp />
            <ToasterWrapper />
          </HashRouter>
        </Suspense>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
